import { LINKED_DATA_SCHEMA } from 'lib/constants/jsonld';
import organizationJson from 'lib/jsonLD/organization.json';
import websiteJson from 'lib/jsonLD/website.json';

// These helpers are for the JSON-LD scripts in the Head component
// They are structured meta data for search engines which helps our SEO
// Tester: https://developers.google.com/search/docs/appearance/structured-data

export const getJsonLDsForSchema = (schema: string) => {
  switch (schema) {
    case LINKED_DATA_SCHEMA.article:
      return {
        organization: true,
        website: true,
        breadcrumb: true,
        article: true,
        linkedData: true,
      };
    case LINKED_DATA_SCHEMA.website:
      return {
        organization: true,
        website: true,
        breadcrumb: false,
        article: false,
        linkedData: true,
      };
    default:
      return {
        organization: false,
        website: false,
        breadcrumb: false,
        article: false,
        linkedData: false,
      };
  }
};

// Used for AuthorBox
export const getPersonJsonLD = (
  personName: string,
  imageUrl: string,
  imageTitle: string,
  socialMediaUrls: string[],
) => {
  const personNameForId = personName.trim().toLowerCase();
  const imageTitleForId = imageTitle.trim().toLowerCase();

  return {
    __html: `{
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://www.smava.de/#/person/${personNameForId}",
    "name": "${personName}",
    "image": {
      "@type": "ImageObject",
      "@id": "https://www.smava.de/#/image/${imageTitleForId}",
      "url": "https:${imageUrl}",
      "caption": "${personName}"
    },
    "sameAs": [
      ${socialMediaUrls.map((url) => `"${url}"`).join(',')}
    ]
  }`,
  };
};

export const getOrganizationJsonLD = () => {
  return {
    __html: JSON.stringify(organizationJson),
  };
};

export const getWebsiteJsonLD = () => {
  return {
    __html: JSON.stringify(websiteJson),
  };
};

export const getBreadcrumbJsonLD = (url: string) => {
  const items = url.split('/').filter((part) => part !== '');

  return {
    __html: `{
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": "https://www.smava.de${url}#breadcrumb",
    "itemListElement": [
  ${items
    .map((item, index) => {
      const url = items.slice(0, index + 1).join('/');
      const name = item.replace(/-/g, ' ');

      return `     {
        "@type": "ListItem",
        "position": ${index + 1},
        "item": {
          "@type": "WebPage",
          "@id": "https://www.smava.de/${url}/",
          "url": "https://www.smava.de/${url}/",
          "name": "${name.toUpperCase()}"
        }
      }`;
    })
    .join(',')}
    ]
  }`,
  };
};

export const getArticleJsonLD = (
  url: string,
  articleSection: string,
  headline: string,
  authorName: string,
  datePublished: string,
  dateModified: string,
) => {
  const capitalizedArticleSection =
    articleSection.charAt(0).toUpperCase() + articleSection.slice(1);
  return {
    __html: `{
    "@type": "Article",
    "@id": "https://www.smava.de${url}#article",
    "isPartOf": {
        "@id": "https://www.smava.de${url}#webpage"
    },
    "datePublished": "${datePublished}",
    "dateModified": "${dateModified}",
    "mainEntityOfPage": {
        "@id": "https://www.smava.de${url}#webpage"
    },
    "publisher": {
        "@id": "https://www.smava.de/#organization"
    },
    "image": {
        "@id": "https://www.smava.de${url}#primaryimage"
    },
    "articleSection": "${capitalizedArticleSection}",
    "headline": "${headline}",
    "author": {
        "@id": "https://www.smava.de/#/person/${authorName.toLocaleLowerCase()}"
    }
  }`,
  };
};
